/**
 * /platform/professionals — manage Professionals (CA organizations).
 * @file src/web/features/platform/pages/professionals.tsx
 *
 * Platform admin: list/search/filter, create (name + owner name + email +
 * phone; temp password auto-generated & revealed/downloadable once), and a
 * detail sidecard that shows ALL fields and supports INLINE editing in
 * place (incl. owner-password reset). "Log in as professional" produces a
 * copyable 1h magic-link (super admin only) to open in another browser.
 * Archive/unarchive and delete (super admin only). Gated by the layout.
 */

import { useEffect, useState } from 'react';
import {
  Building2, Plus, Search, Archive, ArchiveRestore, Trash2, Pencil, X, Loader2,
  CheckCircle2, ShieldAlert, KeyRound, Copy, Download, LogIn, Check,
} from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../../admin/components/AdminPageHeader';
import { PageLoading } from '../../admin/components/PageLoading';
import { adminFetch, adminFetchJson } from '../../admin/lib/admin-api';
import { useAuth } from '../../auth/hooks';

interface Professional {
  id: string; name: string; status: string; plan: string;
  ownerName: string | null; ownerEmail: string; ownerPhone: string | null;
  createdAt: string; ownerActive: boolean | null; lastLogin: string | null;
  legalName: string | null; billingAddress: string | null; registeredAddress: string | null;
  city: string | null; state: string | null; pincode: string | null;
  gstNumber: string | null; gstStatus: string | null; panNumber: string | null;
  bankName: string | null; bankAccountNumber: string | null; bankIfsc: string | null; bankBranch: string | null;
}
interface Page { total: number; page: number; pageSize: number; items: Professional[] }

const PLANS = ['starter', 'trial', 'pro'];
const GST_STATUSES = ['', 'Regular', 'Composition', 'Unregistered', 'SEZ'];
const inputCls = 'w-full rounded-lg border border-ink-200 bg-paper px-3 py-2 text-sm text-ink-900 placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter';
const fmtDate = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

export default function ProfessionalsPage() {
  const { user } = useAuth();
  const isSuper = user?.role === 'admin' && user?.level === 'system';
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('');
  const [data, setData] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [creating, setCreating] = useState(false);
  const [view, setView] = useState<null | { professional: Professional; editing: boolean }>(null);
  const [impersonate, setImpersonate] = useState<null | { professional: Professional; url: string }>(null);
  const [reveal, setReveal] = useState<null | { name: string; email: string; tempPassword: string }>(null);
  const [confirm, setConfirm] = useState<null | { kind: 'delete' | 'archive' | 'reset'; professional: Professional }>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      adminFetchJson<Page>('/api/platform/professionals', { query: { q, status } })
        .then((d) => !cancel && setData(d))
        .finally(() => !cancel && setLoading(false));
    }, q ? 250 : 0);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, status, refreshKey]);

  const refresh = () => setRefreshKey((k) => k + 1);

  // Unarchive is harmless → no confirmation. Archive/delete/reset go
  // through a typed-name confirmation modal (see runConfirm).
  const doUnarchive = async (f: Professional) => {
    await adminFetch(`/api/platform/professionals/${f.id}/unarchive`, { method: 'POST' });
    setView(null); refresh();
  };
  const requestArchive = (f: Professional) => (f.status === 'archived' ? doUnarchive(f) : setConfirm({ kind: 'archive', professional: f }));
  const requestDelete = (f: Professional) => setConfirm({ kind: 'delete', professional: f });
  const requestReset = (f: Professional) => setConfirm({ kind: 'reset', professional: f });

  // Executes the pending confirmed action. Throws on failure so the modal
  // can surface the error (and stays open).
  const runConfirm = async () => {
    if (!confirm) return;
    const f = confirm.professional;
    if (confirm.kind === 'delete') {
      await adminFetch(`/api/platform/professionals/${f.id}`, { method: 'DELETE' });
      setConfirm(null); setView(null); refresh();
    } else if (confirm.kind === 'archive') {
      await adminFetch(`/api/platform/professionals/${f.id}/archive`, { method: 'POST' });
      setConfirm(null); setView(null); refresh();
    } else if (confirm.kind === 'reset') {
      const res = await adminFetchJson<{ email: string; name: string; tempPassword: string }>(`/api/platform/professionals/${f.id}/reset-password`, { method: 'POST' });
      setConfirm(null); setReveal({ name: res.name, email: res.email, tempPassword: res.tempPassword });
    }
  };

  // Build a one-time magic-link that carries the 1h token+user in the URL
  // hash, so it can be opened in a separate browser.
  const doImpersonate = async (f: Professional) => {
    if (busy) return;
    setBusy(true);
    try {
      const res = await adminFetchJson<{ token: string; user: any }>(`/api/platform/professionals/${f.id}/tokenlogin`, { method: 'POST' });
      const d = btoa(unescape(encodeURIComponent(JSON.stringify({ token: res.token, user: res.user }))));
      const url = `${window.location.origin}/auth/tokenlogin#d=${encodeURIComponent(d)}`;
      setImpersonate({ professional: f, url });
    } catch (e: any) {
      alert(e.message?.replace(/^\d+\s/, '') || 'Could not create login link');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <SEO title="Professionals" />
      <AdminPageHeader title="Professionals" breadcrumbs={[{ label: 'Platform' }, { label: 'Professionals' }]} />

      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-full max-w-xs">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search professionals…"
              className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
          </div>
          <select value={status} onChange={(e) => setStatus(e.target.value)}
            className="rounded-xl border border-ink-200 bg-paper py-2.5 pl-3 pr-8 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter">
            <option value="">All</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <button onClick={() => setCreating(true)}
          className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5">
          <Plus className="h-4 w-4" /> New professional
        </button>
      </div>

      {loading && !data ? (
        <PageLoading label="Loading professionals" />
      ) : data && data.items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-ink-300 bg-ink-50/50 px-6 py-16 text-center">
          <span className="mx-auto grid size-12 place-items-center rounded-2xl bg-inkblack text-white"><Building2 className="h-6 w-6" /></span>
          <h3 className="mt-5 text-lg font-semibold text-ink-900">No professionals yet</h3>
          <p className="mt-1.5 text-sm text-ink-600">Onboard your first professional to get started.</p>
          <button onClick={() => setCreating(true)} className="mt-6 inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white"><Plus className="h-4 w-4" /> New professional</button>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full min-w-[46rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Professional</th>
                <th className="px-5 py-3 font-medium">Contact</th>
                <th className="px-5 py-3 font-medium">Plan</th>
                <th className="px-5 py-3 font-medium">Status</th>
                <th className="px-5 py-3 font-medium">Created</th>
                <th className="px-5 py-3 text-right font-medium">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data?.items.map((f) => (
                <tr key={f.id} onClick={() => setView({ professional: f, editing: false })} className="cursor-pointer hover:bg-ink-50/60">
                  <td className="px-5 py-3 font-medium text-ink-900">{f.name}</td>
                  <td className="px-5 py-3 text-ink-600">
                    <div>{f.ownerEmail}</div>
                    <div className="text-xs text-ink-400">{f.ownerPhone || 'No phone'}</div>
                  </td>
                  <td className="px-5 py-3 capitalize text-ink-600">{f.plan}</td>
                  <td className="px-5 py-3"><StatusBadge status={f.status} /></td>
                  <td className="px-5 py-3 text-ink-500">{fmtDate(f.createdAt)}</td>
                  <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                    <div className="flex items-center justify-end gap-1">
                      {isSuper && f.status === 'active' && (
                        <IconBtn title="Log in as this professional" onClick={() => doImpersonate(f)}><LogIn className="h-4 w-4" /></IconBtn>
                      )}
                      <IconBtn title="Edit" onClick={() => setView({ professional: f, editing: true })}><Pencil className="h-4 w-4" /></IconBtn>
                      <IconBtn title={f.status === 'archived' ? 'Unarchive' : 'Archive'} onClick={() => requestArchive(f)}>
                        {f.status === 'archived' ? <ArchiveRestore className="h-4 w-4" /> : <Archive className="h-4 w-4" />}
                      </IconBtn>
                      {isSuper && <IconBtn title="Delete" danger onClick={() => requestDelete(f)}><Trash2 className="h-4 w-4" /></IconBtn>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {view && (
        <ProfessionalDrawer
          professional={view.professional}
          startEditing={view.editing}
          isSuper={isSuper}
          busy={busy}
          onClose={() => setView(null)}
          onSaved={refresh}
          onArchive={() => requestArchive(view.professional)}
          onDelete={() => requestDelete(view.professional)}
          onImpersonate={(f) => doImpersonate(f)}
          onResetPassword={(f) => requestReset(f)}
        />
      )}
      {creating && <CreateModal onClose={() => setCreating(false)} onSaved={() => { setCreating(false); refresh(); }} />}
      {impersonate && <ImpersonateModal professional={impersonate.professional} url={impersonate.url} onClose={() => setImpersonate(null)} />}
      {confirm && <ConfirmModal kind={confirm.kind} professional={confirm.professional} onConfirm={runConfirm} onClose={() => setConfirm(null)} />}
      {reveal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-ink-900/40" onClick={() => setReveal(null)} />
          <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
            <CreatedPanel created={reveal} onDone={() => setReveal(null)} title="Password reset" />
          </div>
        </div>
      )}
    </>
  );
}

function StatusBadge({ status }: { status: string }) {
  return status === 'active'
    ? <span className="inline-flex items-center gap-1 rounded-full bg-ok-50 px-2.5 py-1 text-xs font-medium text-ok-600"><CheckCircle2 className="h-3.5 w-3.5" /> Active</span>
    : <span className="rounded-full bg-ink-100 px-2.5 py-1 text-xs font-medium text-ink-500">Archived</span>;
}

function IconBtn({ children, title, danger, onClick }: { children: React.ReactNode; title: string; danger?: boolean; onClick: () => void }) {
  return (
    <button title={title} onClick={onClick}
      className={`grid size-8 place-items-center rounded-lg transition-colors ${danger ? 'text-ink-400 hover:bg-danger-50 hover:text-danger-600' : 'text-ink-400 hover:bg-ink-100 hover:text-ink-700'}`}>
      {children}
    </button>
  );
}

/* ── detail sidecard with inline editing ───────────────────────── */

const EDITABLE_KEYS = [
  'name', 'ownerName', 'ownerPhone', 'plan', 'legalName', 'gstNumber', 'gstStatus',
  'panNumber', 'billingAddress', 'registeredAddress', 'city', 'state', 'pincode',
  'bankName', 'bankAccountNumber', 'bankIfsc', 'bankBranch',
] as const;

function ProfessionalDrawer({
  professional, startEditing, isSuper, busy, onClose, onSaved, onArchive, onDelete, onImpersonate, onResetPassword,
}: {
  professional: Professional; startEditing: boolean; isSuper: boolean; busy: boolean;
  onClose: () => void; onSaved: () => void; onArchive: () => void; onDelete: () => void;
  onImpersonate: (f: Professional) => void; onResetPassword: (f: Professional) => void;
}) {
  const [current, setCurrent] = useState<Professional>(professional);
  const [editing, setEditing] = useState(startEditing);
  const [form, setForm] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [gstin, setGstin] = useState('');
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMsg, setGstMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const beginEdit = () => {
    const f: Record<string, string> = {};
    for (const k of EDITABLE_KEYS) f[k] = (current[k as keyof Professional] as string) ?? '';
    setForm(f); setError(null); setGstin(current.gstNumber ?? ''); setGstMsg(null); setEditing(true);
  };
  // Enter edit mode immediately if opened via the Edit action.
  useEffect(() => { if (startEditing) beginEdit(); /* eslint-disable-next-line */ }, []);

  const set = (k: string, v: string) => setForm((p) => ({ ...p, [k]: v }));

  // Pull GST details into the edit form (for professionals created manually).
  const lookupGst = async () => {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) { setGstMsg({ tone: 'err', text: 'Enter a 15-character GSTIN.' }); return; }
    setGstLoading(true); setGstMsg(null);
    try {
      const res = await adminFetchJson<{ data: any }>(`/api/gst/lookup/${encodeURIComponent(g)}`);
      const d = res.data;
      setForm((p) => ({
        ...p,
        name: d.tradeName || d.legalName || p.name,
        legalName: d.legalName ?? p.legalName,
        gstNumber: d.gstin ?? p.gstNumber,
        gstStatus: d.businessType ?? p.gstStatus,
        billingAddress: d.address?.full ?? p.billingAddress,
        city: d.address?.city ?? p.city,
        state: d.address?.state ?? p.state,
        pincode: d.address?.pincode ?? p.pincode,
      }));
      setGstMsg({ tone: 'ok', text: `${d.legalName}${d.status ? ` · ${d.status}` : ''} — fields updated below` });
    } catch (e: any) {
      setGstMsg({ tone: 'err', text: e.message?.replace(/^\d+\s/, '') || 'Lookup failed' });
    } finally {
      setGstLoading(false);
    }
  };

  const save = async () => {
    setError(null); setSaving(true);
    try {
      await adminFetchJson(`/api/platform/professionals/${current.id}`, {
        method: 'PATCH',
        body: JSON.stringify(form),
      });
      setCurrent((c) => ({ ...c, ...(form as any) }));
      setEditing(false);
      onSaved();
    } catch (e: any) {
      setError(e.message?.replace(/^\d+\s/, '') || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const val = (k: keyof Professional) => (editing ? form[k as string] ?? '' : (current[k] as string) ?? '');

  return (
    <div className="fixed inset-0 z-40">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <aside className="absolute inset-y-0 right-0 flex w-full max-w-md flex-col border-l border-ink-200 bg-paper shadow-xl">
        {/* header */}
        <div className="flex items-start justify-between border-b border-ink-100 px-6 py-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="grid size-11 shrink-0 place-items-center rounded-xl bg-inkblack text-sm font-semibold text-white">
              {current.name.slice(0, 2).toUpperCase()}
            </span>
            <div className="min-w-0">
              <h3 className="truncate text-base font-semibold text-ink-900">{current.name}</h3>
              <div className="mt-1"><StatusBadge status={current.status} /></div>
            </div>
          </div>
          <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
        </div>

        {/* body */}
        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto px-6 py-5">
          {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}

          {!editing && isSuper && current.status === 'active' && (
            <button onClick={() => onImpersonate(current)} disabled={busy}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-highlighter bg-highlighter-40 px-4 py-3 text-sm font-semibold text-inkblack transition hover:bg-highlighter-80 disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <LogIn className="h-4 w-4" />}
              Log in as this professional
            </button>
          )}

          {editing && (
            <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3.5">
              <label className="text-sm font-medium text-ink-700">Pull from GST</label>
              <div className="mt-1.5 flex gap-2">
                <input value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupGst(); } }}
                  placeholder="29ABCDE1234F1Z5"
                  className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-paper px-3 py-2 font-mono text-sm text-ink-900 placeholder:font-sans placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
                <button type="button" onClick={lookupGst} disabled={gstLoading}
                  className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-ink-900 px-3.5 text-sm font-semibold text-white transition hover:bg-ink-800 disabled:opacity-60">
                  {gstLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Fetch
                </button>
              </div>
              {gstMsg && <p className={`mt-1.5 text-xs ${gstMsg.tone === 'ok' ? 'text-ok-600' : 'text-danger-600'}`}>{gstMsg.text}</p>}
            </div>
          )}

          <DSection title="Organization">
            <EField label="Name" value={val('name')} editing={editing} onChange={(v) => set('name', v)} />
            <EField label="Legal name" value={val('legalName')} editing={editing} onChange={(v) => set('legalName', v)} />
            <EField label="Plan" value={val('plan')} editing={editing} onChange={(v) => set('plan', v)} options={PLANS} />
          </DSection>

          <DSection title="Owner login">
            <EField label="Owner name" value={val('ownerName')} editing={editing} onChange={(v) => set('ownerName', v)} />
            <EField label="Email" value={current.ownerEmail} editing={editing} readOnly />
            <EField label="Phone" value={val('ownerPhone')} editing={editing} onChange={(v) => set('ownerPhone', v)} />
            {isSuper && (
              <div className="flex items-center gap-3 py-2 text-sm">
                <span className="w-24 shrink-0 text-ink-500">Password</span>
                <button onClick={() => onResetPassword(current)}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-ink-200 px-2.5 py-1.5 text-xs font-semibold text-ink-600 transition hover:bg-ink-50 hover:text-ink-900">
                  <KeyRound className="h-3.5 w-3.5" /> Reset to a new random password
                </button>
              </div>
            )}
          </DSection>

          <DSection title="Registration">
            <EField label="GST number" value={val('gstNumber')} editing={editing} onChange={(v) => set('gstNumber', v)} mono />
            <EField label="GST status" value={val('gstStatus')} editing={editing} onChange={(v) => set('gstStatus', v)} options={GST_STATUSES} />
            <EField label="PAN" value={val('panNumber')} editing={editing} onChange={(v) => set('panNumber', v)} mono />
          </DSection>

          <DSection title="Address">
            <EField label="Billing" value={val('billingAddress')} editing={editing} onChange={(v) => set('billingAddress', v)} textarea />
            <EField label="Registered" value={val('registeredAddress')} editing={editing} onChange={(v) => set('registeredAddress', v)} textarea />
            <EField label="City" value={val('city')} editing={editing} onChange={(v) => set('city', v)} />
            <EField label="State" value={val('state')} editing={editing} onChange={(v) => set('state', v)} />
            <EField label="Pincode" value={val('pincode')} editing={editing} onChange={(v) => set('pincode', v)} />
          </DSection>

          <DSection title="Bank">
            <EField label="Bank name" value={val('bankName')} editing={editing} onChange={(v) => set('bankName', v)} />
            <EField label="Account no." value={val('bankAccountNumber')} editing={editing} onChange={(v) => set('bankAccountNumber', v)} mono />
            <EField label="IFSC code" value={val('bankIfsc')} editing={editing} onChange={(v) => set('bankIfsc', v)} mono />
            <EField label="Location" value={val('bankBranch')} editing={editing} onChange={(v) => set('bankBranch', v)} />
          </DSection>

          {!editing && (
            <DSection title="Activity">
              <EField label="Login" value={current.ownerActive === false ? 'Disabled' : 'Active'} editing={false} readOnly />
              <EField label="Last login" value={fmtDate(current.lastLogin)} editing={false} readOnly />
              <EField label="Onboarded" value={fmtDate(current.createdAt)} editing={false} readOnly />
            </DSection>
          )}
        </div>

        {/* footer actions */}
        <div className="flex flex-wrap items-center gap-2 border-t border-ink-100 px-6 py-4">
          {editing ? (
            <>
              <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2 text-sm font-semibold text-white disabled:opacity-60">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
              </button>
              <button onClick={() => { setEditing(false); setError(null); }} disabled={saving} className="rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">
                Cancel
              </button>
            </>
          ) : (
            <>
              <button onClick={beginEdit} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-white">
                <Pencil className="h-4 w-4" /> Edit
              </button>
              <button onClick={onArchive} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">
                {current.status === 'archived' ? <><ArchiveRestore className="h-4 w-4" /> Unarchive</> : <><Archive className="h-4 w-4" /> Archive</>}
              </button>
              {isSuper && (
                <button onClick={onDelete} className="ml-auto inline-flex items-center gap-2 rounded-full border border-danger-200 bg-paper px-4 py-2 text-sm font-semibold text-danger-600 hover:bg-danger-50">
                  <Trash2 className="h-4 w-4" /> Delete
                </button>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}

function DSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-ink-200 p-4">
      <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-ink-400">{title}</p>
      <div className="divide-y divide-ink-50">{children}</div>
    </div>
  );
}

function EField({ label, value, editing, onChange, type = 'text', options, textarea, readOnly, mono, hint }: {
  label: string; value: string; editing: boolean; onChange?: (v: string) => void;
  type?: string; options?: string[]; textarea?: boolean; readOnly?: boolean; mono?: boolean; hint?: string;
}) {
  const showInput = editing && !readOnly && onChange;
  return (
    <div className="flex items-start gap-3 py-2 text-sm">
      <span className="w-24 shrink-0 pt-2 text-ink-500">{label}</span>
      <div className="min-w-0 flex-1">
        {!showInput ? (
          <div className={`whitespace-pre-wrap break-words pt-1.5 font-medium ${value ? 'text-ink-800' : 'text-ink-300'} ${mono ? 'font-mono' : ''}`}>
            {value || '—'}
          </div>
        ) : options ? (
          <select value={value} onChange={(e) => onChange!(e.target.value)} className={`${inputCls} capitalize`}>
            {options.map((o) => <option key={o} value={o}>{o || '—'}</option>)}
          </select>
        ) : textarea ? (
          <textarea rows={2} value={value} onChange={(e) => onChange!(e.target.value)} className={`${inputCls} resize-y`} />
        ) : (
          <>
            <input type={type} value={value} onChange={(e) => onChange!(e.target.value)} className={inputCls} />
            {hint && <p className="mt-1 text-xs text-ink-400">{hint}</p>}
          </>
        )}
      </div>
    </div>
  );
}

/* ── impersonation: copyable magic-link ────────────────────────── */

function ImpersonateModal({ professional, url, onClose }: { professional: Professional; url: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* noop */ }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-xl bg-highlighter-40 text-inkblack"><LogIn className="h-5 w-5" /></span>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-ink-900">Log in as {professional.name}</h3>
            <p className="text-sm text-ink-500">Open this link in another browser</p>
          </div>
        </div>

        <p className="mb-3 flex items-start gap-2 rounded-xl border border-warn-200 bg-warn-50 px-3.5 py-2.5 text-xs text-warn-700">
          <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          Anyone with this link can sign in as this professional for the next hour. Share it carefully.
        </p>

        <label className="text-xs font-medium uppercase tracking-wide text-ink-400">Login link</label>
        <div className="mt-1.5 flex items-stretch gap-2">
          <input readOnly value={url} onFocus={(e) => e.currentTarget.select()}
            className="min-w-0 flex-1 rounded-lg border border-ink-200 bg-ink-50 px-3 py-2 font-mono text-xs text-ink-700 focus:border-inkblack focus:outline-none" />
          <button onClick={copy} className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-inkblack px-3 text-sm font-semibold text-white">
            {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied' : 'Copy'}
          </button>
        </div>

        <div className="mt-6 flex justify-end">
          <button onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-5 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">Done</button>
        </div>
      </div>
    </div>
  );
}

/* ── typed-name confirmation ───────────────────────────────────── */

const CONFIRM_CFG = {
  delete: {
    title: 'Delete professional', confirmLabel: 'Delete', danger: true, icon: Trash2,
    body: (n: string) => <>This permanently deletes <strong>{n}</strong> and its owner login. This cannot be undone.</>,
  },
  archive: {
    title: 'Archive professional', confirmLabel: 'Archive', danger: false, icon: Archive,
    body: (n: string) => <>Archiving disables <strong>{n}</strong>'s owner login until you unarchive it.</>,
  },
  reset: {
    title: 'Reset owner password', confirmLabel: 'Reset password', danger: false, icon: KeyRound,
    body: (n: string) => <>A new random password is generated for <strong>{n}</strong>. The current password stops working immediately.</>,
  },
} as const;

function ConfirmModal({ kind, professional, onConfirm, onClose }: {
  kind: 'delete' | 'archive' | 'reset'; professional: Professional; onConfirm: () => Promise<void>; onClose: () => void;
}) {
  const cfg = CONFIRM_CFG[kind];
  const Icon = cfg.icon;
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const ok = text.trim() === professional.name;

  const go = async () => {
    if (!ok || busy) return;
    setBusy(true); setError(null);
    try {
      await onConfirm(); // resolves after the parent unmounts this modal on success
    } catch (e: any) {
      setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong');
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        <div className="mb-4 flex items-center gap-3">
          <span className={`grid size-10 place-items-center rounded-xl ${cfg.danger ? 'bg-danger-50 text-danger-600' : 'bg-ink-100 text-ink-600'}`}>
            <Icon className="h-5 w-5" />
          </span>
          <h3 className="text-lg font-semibold text-ink-900">{cfg.title}</h3>
        </div>

        <p className="text-sm text-ink-600">{cfg.body(professional.name)}</p>

        {error && <div className="mt-3 rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}

        <label className="mt-4 block text-sm text-ink-600">
          Type <span className="font-semibold text-ink-900">{professional.name}</span> to confirm
        </label>
        <input
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') go(); }}
          placeholder={professional.name}
          style={{ outline: 'none', boxShadow: 'none' }}
          className="mt-1.5 w-full rounded-xl border border-ink-200 bg-paper px-4 py-2.5 text-sm text-ink-900 placeholder:text-ink-400 focus:border-ink-300 focus-visible:outline-none"
        />

        <div className="mt-6 flex justify-end gap-2">
          <button onClick={onClose} disabled={busy} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
          <button
            onClick={go}
            disabled={!ok || busy}
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50 ${cfg.danger ? 'bg-danger-600 hover:bg-danger-700' : 'bg-inkblack'}`}
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />} {cfg.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── create (simple) ───────────────────────────────────────────── */

function CreateModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [form, setForm] = useState({ name: '', ownerName: '', ownerEmail: '', ownerPhone: '' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [created, setCreated] = useState<null | { name: string; email: string; tempPassword: string }>(null);

  // GSTIN-driven autofill.
  const [gstin, setGstin] = useState('');
  const [gst, setGst] = useState<Record<string, string> | null>(null);
  const [gstLoading, setGstLoading] = useState(false);
  const [gstMsg, setGstMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);

  const set = (k: string, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const setG = (k: string, v: string) => setGst((g) => ({ ...(g ?? {}), [k]: v }));

  const lookupGst = async () => {
    const g = gstin.trim().toUpperCase();
    if (g.length !== 15) { setGstMsg({ tone: 'err', text: 'Enter a 15-character GSTIN.' }); return; }
    setGstLoading(true); setGstMsg(null);
    try {
      const res = await adminFetchJson<{ data: any }>(`/api/gst/lookup/${encodeURIComponent(g)}`);
      const d = res.data;
      setForm((f) => ({ ...f, name: d.tradeName || d.legalName || f.name }));
      setGst({
        gstNumber: d.gstin,
        legalName: d.legalName ?? '',
        gstStatus: d.businessType ?? '',
        billingAddress: d.address?.full ?? '',
        city: d.address?.city ?? '',
        state: d.address?.state ?? '',
        pincode: d.address?.pincode ?? '',
      });
      setGstMsg({ tone: 'ok', text: `${d.legalName}${d.status ? ` · ${d.status}` : ''}` });
    } catch (e: any) {
      setGst(null);
      setGstMsg({ tone: 'err', text: e.message?.replace(/^\d+\s/, '') || 'Lookup failed' });
    } finally {
      setGstLoading(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null); setSaving(true);
    try {
      const res = await adminFetchJson<{ id: string; tempPassword: string }>('/api/platform/professionals', {
        method: 'POST',
        body: JSON.stringify({ ...form, ...(gst ?? {}) }),
      });
      setCreated({ name: form.name, email: form.ownerEmail.trim().toLowerCase(), tempPassword: res.tempPassword });
    } catch (e: any) {
      setError(e.message?.replace(/^\d+\s/, '') || 'Something went wrong');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-ink-900/40" onClick={created ? onSaved : onClose} />
      <div className="relative z-10 w-full max-w-md rounded-2xl border border-ink-200 bg-paper p-7 shadow-xl">
        {created ? (
          <CreatedPanel created={created} onDone={onSaved} />
        ) : (
          <>
            <div className="mb-5 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-ink-900">Onboard a professional</h3>
              <button onClick={onClose} className="text-ink-400 hover:text-ink-700"><X className="h-5 w-5" /></button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              {error && <div className="rounded-xl border border-danger-200 bg-danger-50 px-3.5 py-2.5 text-sm text-danger-700">{error}</div>}

              {/* GSTIN autofill — prefills name, GST, address, city, state, pincode. */}
              <div className="rounded-xl border border-ink-200 bg-ink-50/60 p-3.5">
                <label className="text-sm font-medium text-ink-700">GSTIN <span className="font-normal text-ink-400">— autofills details</span></label>
                <div className="mt-1.5 flex gap-2">
                  <input value={gstin} maxLength={15} onChange={(e) => setGstin(e.target.value.toUpperCase())}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); lookupGst(); } }}
                    placeholder="29ABCDE1234F1Z5"
                    className="min-w-0 flex-1 rounded-xl border border-ink-200 bg-paper px-3.5 py-2.5 font-mono text-sm text-ink-900 placeholder:font-sans placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
                  <button type="button" onClick={lookupGst} disabled={gstLoading}
                    className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-ink-900 px-4 text-sm font-semibold text-white transition hover:bg-ink-800 disabled:opacity-60">
                    {gstLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Fetch
                  </button>
                </div>
                {gstMsg && <p className={`mt-1.5 text-xs ${gstMsg.tone === 'ok' ? 'text-ok-600' : 'text-danger-600'}`}>{gstMsg.text}</p>}
              </div>

              <CField label="Professional organization name" value={form.name} onChange={(v) => set('name', v)} required />
              <CField label="Owner name" value={form.ownerName} onChange={(v) => set('ownerName', v)} />
              <CField label="Owner email (login)" type="email" value={form.ownerEmail} onChange={(v) => set('ownerEmail', v)} required />
              <CField label="Phone" type="tel" value={form.ownerPhone} onChange={(v) => set('ownerPhone', v)} placeholder="Optional" />

              {gst && (
                <div className="space-y-3 rounded-xl border border-ink-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-wide text-ink-400">Fetched from GST — review &amp; edit</p>
                  <CField label="Legal name" value={gst.legalName ?? ''} onChange={(v) => setG('legalName', v)} />
                  <div className="grid grid-cols-2 gap-3">
                    <CField label="GST number" value={gst.gstNumber ?? ''} onChange={(v) => setG('gstNumber', v)} />
                    <CField label="GST status" value={gst.gstStatus ?? ''} onChange={(v) => setG('gstStatus', v)} />
                  </div>
                  <CField label="Billing address" value={gst.billingAddress ?? ''} onChange={(v) => setG('billingAddress', v)} />
                  <div className="grid grid-cols-3 gap-3">
                    <CField label="City" value={gst.city ?? ''} onChange={(v) => setG('city', v)} />
                    <CField label="State" value={gst.state ?? ''} onChange={(v) => setG('state', v)} />
                    <CField label="Pincode" value={gst.pincode ?? ''} onChange={(v) => setG('pincode', v)} />
                  </div>
                </div>
              )}

              <div className="flex items-start gap-2 rounded-xl bg-ink-50 px-3.5 py-3 text-xs text-ink-500">
                <KeyRound className="mt-0.5 h-3.5 w-3.5 shrink-0 text-ink-400" />
                <p>
                  A temporary password is generated automatically and shown once after creation. Plan defaults to{' '}
                  <span className="font-medium text-ink-600">Starter</span>. Add tax, address &amp; bank details later from the sidecard.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button type="button" onClick={onClose} className="rounded-full border border-ink-300 bg-paper px-5 py-2.5 text-sm font-semibold text-ink-700 hover:bg-ink-50">Cancel</button>
                <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-5 py-2.5 text-sm font-semibold text-white disabled:opacity-60">
                  {saving && <Loader2 className="h-4 w-4 animate-spin" />} Create professional
                </button>
              </div>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

function CField({ label, value, onChange, type = 'text', required, placeholder }: {
  label: string; value: string; onChange: (v: string) => void; type?: string; required?: boolean; placeholder?: string;
}) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-ink-700">{label}</label>
      <input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} placeholder={placeholder}
        className="w-full rounded-xl border border-ink-200 bg-paper px-4 py-3 text-sm text-ink-900 placeholder:text-ink-400 focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter" />
    </div>
  );
}

/* ── created: reveal + download generated credentials ──────────── */

function CreatedPanel({ created, onDone, title = 'Professional created' }: { created: { name: string; email: string; tempPassword: string }; onDone: () => void; title?: string }) {
  const [copied, setCopied] = useState(false);
  const loginUrl = `${window.location.origin}/login`;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(`Email: ${created.email}\nTemporary password: ${created.tempPassword}`);
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { /* clipboard unavailable */ }
  };

  const download = () => {
    const body =
      `oddits — Professional login credentials\n` +
      `========================================\n\n` +
      `Organization : ${created.name}\n` +
      `Login URL : ${loginUrl}\n` +
      `Email : ${created.email}\n` +
      `Temp password: ${created.tempPassword}\n\n` +
      `Please sign in and change this password on first login.\n`;
    const url = URL.createObjectURL(new Blob([body], { type: 'text/plain' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `oddits-${created.name.toLowerCase().replace(/[^a-z0-9]+/g, '-')}-credentials.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="mb-4 flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-xl bg-ok-50 text-ok-600"><CheckCircle2 className="h-5 w-5" /></span>
        <div>
          <h3 className="text-lg font-semibold text-ink-900">{title}</h3>
          <p className="text-sm text-ink-500">{created.name}</p>
        </div>
      </div>

      <p className="mb-3 flex items-start gap-2 rounded-xl border border-warn-200 bg-warn-50 px-3.5 py-2.5 text-xs text-warn-700">
        <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        Save these now — the temporary password is shown only once and cannot be retrieved later.
      </p>

      <div className="space-y-2 rounded-xl border border-ink-200 bg-ink-50 p-4 text-sm">
        <div className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-ink-500">Email</span>
          <span className="min-w-0 flex-1 break-all font-medium text-ink-800">{created.email}</span>
        </div>
        <div className="flex items-center gap-3">
          <span className="w-20 shrink-0 text-ink-500">Password</span>
          <span className="min-w-0 flex-1 break-all font-mono font-semibold text-ink-900">{created.tempPassword}</span>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap gap-2">
        <button onClick={copy} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">
          {copied ? <Check className="h-4 w-4 text-ok-600" /> : <Copy className="h-4 w-4" />} {copied ? 'Copied' : 'Copy'}
        </button>
        <button onClick={download} className="inline-flex items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 hover:bg-ink-50">
          <Download className="h-4 w-4" /> Download
        </button>
        <button onClick={onDone} className="ml-auto rounded-full bg-inkblack px-5 py-2 text-sm font-semibold text-white">Done</button>
      </div>
    </div>
  );
}
